<template>
    <div class="box m-b-0 box-modal">
        <div class="box-header">
            <h3>Znajdź klienta</h3>
        </div>
        <div class="box-tool">
            <ul class="nav">
                <li class="nav-item inline icon-with-desc">
                    <a class="nav-link text-muted" @click="$emit('close')">
                        <i class="material-icons md-18">clear</i>
                        <span class="icon-desc">ESC</span>
                    </a>
                </li>
            </ul>
        </div>
        <div style="min-height:400px; max-height:600px;" class="box-body">
            <div class="form-group">
                <label>Wyszukaj klienta</label>
                <input 
                    type="text" 
                    class="form-control" 
                    v-model="searchQuery"
                    @input="onSearchInput"
                    placeholder="Wprowadź nazwę, email, telefon lub NIP..."
                    ref="searchInput"
                    autofocus
                />
            </div>

            <div v-if="isLoading" class="text-center p-3">
                <i class="material-icons rotating">refresh</i>
                <p class="text-muted">Wyszukiwanie...</p>
            </div>

            <div v-else-if="customers.length > 0" style="max-height: 450px; overflow-y: auto;">
                <div 
                    v-for="customer in customers" 
                    :key="customer._id"
                    class="customer-card"
                    @click="selectCustomer(customer._id)"
                >
                    <div class="customer-card-header">
                        <strong>{{ customer.name }}</strong>
                        <button class="btn btn-sm btn-primary" @click.stop="selectCustomer(customer._id)">
                            <i class="material-icons md-18">check</i>
                            Wybierz
                        </button>
                    </div>
                    <div class="customer-card-body">
                        <div class="customer-info" v-if="customer.email">
                            <i class="material-icons md-18">email</i>
                            <span>{{ customer.email }}</span>
                        </div>
                        <div class="customer-info" v-if="customer.phone">
                            <i class="material-icons md-18">phone</i>
                            <span>{{ customer.phone }}</span>
                        </div>
                        <div class="customer-info" v-if="customer.city || customer.street">
                            <i class="material-icons md-18">location_on</i>
                            <span>
                                <template v-if="customer.street">{{ customer.street }}, </template>
                                <template v-if="customer.postCode">{{ customer.postCode }} </template>
                                {{ customer.city }}
                            </span>
                        </div>
                        <div class="customer-info" v-if="customer.taxNo">
                            <i class="material-icons md-18">receipt</i>
                            <span>NIP: {{ customer.taxNo }}</span>
                        </div>
                    </div>
                </div>
                <div v-if="total > customers.length" class="text-center text-muted p-2">
                    Pokazano {{ customers.length }} z {{ total }} wyników
                </div>
            </div>

            <div v-else-if="searchQuery && !isLoading" class="text-center p-4 text-muted">
                <i class="material-icons md-48">search_off</i>
                <p>Nie znaleziono klientów</p>
            </div>

            <div v-else class="text-center p-4 text-muted">
                <i class="material-icons md-48">search</i>
                <p>Wprowadź frazę wyszukiwania</p>
            </div>
        </div>
        <div class="box-footer clearfix">
            <button class="btn white none-radius btn-sm" @click="$emit('close')">Anuluj</button>
        </div>
    </div>
</template>
<script>
import Api from '@/Api'

export default {
    name: 'SelectCustomerModal',
    props: {
    },
    components: {
    },
    data() {
        return {
            searchQuery: '',
            customers: [],
            total: 0,
            isLoading: false,
            searchTimeout: null
        }
    },
    created: function() {
        window.addEventListener('keydown', this.handleKeyUp);
    },
    mounted: function() {
        // Focus na polu wyszukiwania
        this.$nextTick(() => {
            if (this.$refs.searchInput) {
                this.$refs.searchInput.focus();
            }
        });
    },
    destroyed: function() {
        window.removeEventListener('keydown', this.handleKeyUp);
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
    },
    methods: {
        onSearchInput: function() {
            // Debouncing - czekamy 300ms po zakończeniu pisania
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }

            if (!this.searchQuery || this.searchQuery.length < 2) {
                this.customers = [];
                this.total = 0;
                return;
            }

            this.searchTimeout = setTimeout(() => {
                this.searchCustomers();
            }, 300);
        },

        searchCustomers: async function() {
            if (!this.searchQuery || this.searchQuery.length < 2) {
                return;
            }

            this.isLoading = true;

            try {
                const filters = {
                    query: this.searchQuery,
                    limit: 50,
                    offset: 0
                };

                const response = await Api.customer.search(filters);
                
                this.customers = response.data || [];
                this.total = response.total || 0;
            } catch (error) {
                console.error('Błąd wyszukiwania klientów:', error);
                this.$snotify.error('Błąd podczas wyszukiwania klientów');
                this.customers = [];
                this.total = 0;
            } finally {
                this.isLoading = false;
            }
        },

        selectCustomer: function(customerId) {
            this.$emit('close', customerId);
        },

        handleKeyUp: function(e) {
            if (e.keyCode == 27) { // ESC
                e.preventDefault();
                this.$emit('close');
            }
        }
    }
}
</script>
<style scoped>
@keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

.rotating {
    animation: rotate 1s linear infinite;
}

.customer-card {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 12px;
    margin-bottom: 10px;
    cursor: pointer;
    transition: all 0.2s;
    background-color: var(--dm-surface, #fff);
}

.customer-card:hover {
    background-color: var(--dm-surface-2, #f5f5f5);
    border-color: #2196F3;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.customer-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e0e0e0;
}

.customer-card-header strong {
    font-size: 16px;
    color: var(--dm-text, #333);
}

.customer-card-body {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.customer-info {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #666;
    font-size: 14px;
}

.customer-info i {
    color: var(--dm-text-muted, #999);
    flex-shrink: 0;
}

.customer-info span {
    word-break: break-word;
}
</style>
